import ClearIcon from "@mui/icons-material/Clear";
import {
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { useRecordEntityOptions } from "src/hook/useRecordEntityOptions";
import { EntityOptionsSource } from "src/utils/api/dashboard";

type EntityFilterProps = {
  source: EntityOptionsSource;
  // Table scope only: omit pagination and selected entity IDs so all matching
  // entities remain available across pages and while selecting multiple names.
  filters: Record<string, any>;
  value: string[];
  onChange: (ids: string[]) => void;
  enabled?: boolean;
};

const EntityFilter = ({
  source,
  filters,
  value,
  onChange,
  enabled = true,
}: EntityFilterProps) => {
  const { entityInfo, isFetching, isError } = useRecordEntityOptions(
    source,
    filters,
    enabled
  );

  return (
    <FormControl sx={{ width: 240, flexShrink: 0 }} size="small">
      <Select
        multiple
        displayEmpty
        value={value}
        inputProps={{ "aria-label": "Filter By Entity" }}
        onChange={(event) => {
          const selected = event.target.value;
          onChange(
            typeof selected === "string" ? selected.split(",") : selected
          );
        }}
        renderValue={(selected) =>
          selected.length
            ? `Filter By Entity (${selected.length})`
            : "Filter By Entity"
        }
        sx={{
          background: "white",
          "& .MuiSelect-select": {
            pr: value.length ? "64px !important" : undefined,
          },
        }}
        endAdornment={
          value.length > 0 ? (
            <InputAdornment position="end" sx={{ mr: 2 }}>
              <IconButton
                size="small"
                aria-label="Clear Filter By Entity"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange([]);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
        MenuProps={{ PaperProps: { sx: { maxHeight: 280, maxWidth: 480 } } }}
      >
        {entityInfo.map((entity) => (
          <MenuItem key={entity.id} value={entity.id}>
            <Checkbox checked={value.includes(entity.id)} />
            <ListItemText primary={entity.name} />
          </MenuItem>
        ))}
        {!entityInfo.length && (
          <MenuItem disabled>
            {isFetching
              ? "Loading entities..."
              : isError
              ? "Unable to load entities"
              : "No entities found"}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default EntityFilter;
